import React from 'react';
import { FormattedHTMLMessage, injectIntl } from 'react-intl';

import {
    VerificationService,
    FieldValidationErrors,
    ExtendedFieldValidationErrors,
    FormSelectChoice,
    Organization,
    InputSelectOnKeyDown,
    MedicalProfessionalPersonalInfoViewModel,
    MedicalProfessionalPersonalInfoResponse,
    setFocus,
    setRef,
    hasFailedInstantMatch,
    isFormFilled,
    VerificationStepsEnum,
    MedicalProfessionalStatusDefaultMessagesEnum,
    handleEmailOnKeyDown,
    submitForm,
    updateFieldValidationErrorsByFieldId,
    updateViewModelOrganization,
    produceDraftViewModel,
    PhoneNumberComponent as PhoneNumber,
    FirstNameComponent as FirstName,
    LastNameComponent as LastName,
    EmailComponent as Email,
    MarketConsentWrapper,
    PostalCodeComponent as PostalCode,
    CountryComponentWrapper,
    FieldId,
    MedicalProfessionalStatus,
    getSafe,
} from '@sheerid/jslib';

import { LogoComponent } from '../LogoComponent/LogoComponent';
import { CaptainsCircleComponent } from '../FormFields/CaptainsCircleComponent';
import { FormFooterComponent as FormFooter } from '../FormFooter/FormFooterComponent';
import { MedicalProfessionalOrganizationComponent as MedicalProfessionalOrganization } from '../FormFields/MedicalProfessionalOrganizationComponent';
import { MedicalStatusComponent as MedicalStatus } from '../FormFields/MedicalStatusComponent';
import { BirthDateComponent as BirthDate } from '../FormFields/BirthDate/BirthDateComponent';
import { HowDoesVerifyingWorkComponent as HowDoesVerifyingWork } from '../HowDoesVerifyingWork/HowDoesVerifyingWorkComponent';
import { CityComponent } from '../FormFields/City';
import { StateComponent } from '../FormFields/State';
import { AddressComponent } from '../FormFields/AddressComponent';

interface StepMedicalProfessionalPersonalInfoComponentProps {
    verificationService: VerificationService;
    intl: any;
}

export const StepMedicalProfessionalPersonalInfo = ({ intl, verificationService }: StepMedicalProfessionalPersonalInfoComponentProps) => {
    const viewModel = verificationService.viewModel as MedicalProfessionalPersonalInfoViewModel;
    const fieldValidationErrors: FieldValidationErrors & ExtendedFieldValidationErrors = verificationService.fieldValidationErrors;
    const verificationResponse = verificationService.verificationResponse as MedicalProfessionalPersonalInfoResponse;
    const failedInstantMatch = hasFailedInstantMatch(verificationResponse);

    const updateMedicalProfessionalViewModel = (key: keyof MedicalProfessionalPersonalInfoViewModel, value: any) => {
        const nextState = produceDraftViewModel(viewModel, key, value);
        verificationService.updateViewModel(nextState);
    };

    const getAvailableMedicalStatuses = () => {
        const availableStatusesResponse = verificationResponse.availableStatuses;
        const availableStatuses: FormSelectChoice[] = [];

        for (const status of availableStatusesResponse as [MedicalProfessionalStatus]) {
            availableStatuses.push({
                value: status,
                label: intl.formatMessage({ id: status, defaultMessage: MedicalProfessionalStatusDefaultMessagesEnum[status] }),
            });
        }
        return availableStatuses;
    };

    const handleMedicalStatusOnKeyDown: InputSelectOnKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Tab' && !event.shiftKey) {
            event.preventDefault();
            setFocus('organization');
        }
    };

    return (
        <div id="sid-step-active-medical-professional-personal-info" className="sid-l-container" role="main" tabIndex={0} aria-labelledby="sidHeader">
            { failedInstantMatch ?
                <div className="sid-header" id="sidHeader">
                    <div className="sid-l-horz-center">
                        <LogoComponent verificationService={verificationService} />
                    </div>
                    <div className="sid-header__title sid-l-horz-center">
                        <FormattedHTMLMessage id="step.personalInfo.tryAgain.title" defaultMessage="Check Your Name, Email & Organization" />
                    </div>
                    <div className="sid-header__subtitle sid-l-horz-center sid-header__subtitle--error">
                        <FormattedHTMLMessage id="step.personalInfo.tryAgain.subtitle"
                            defaultMessage="Your full name, organization name, and email must match what is on record with your agency." />
                    </div>
                </div>
            :
                <div className="sid-header" id="sidHeader">
                    <div className="sid-l-horz-center">
                        <LogoComponent verificationService={verificationService} />
                    </div>
                    <div className="sid-header__title sid-l-horz-center">
                        <h1 role="status">
                            <FormattedHTMLMessage id="step.personalInfo.title" defaultMessage="Unlock this Medical Professional-Only Offer" />
                        </h1>
                    </div>
                    <div className="sid-header__subtitle sid-l-horz-center">
                        <h2 role="status">
                            <FormattedHTMLMessage id="step.personalInfo.subtitle"
                                defaultMessage="Verify you're a licensed medical professional." />
                        </h2>
                    </div>
                    <div className="sid-header__how-verifying-works sid-l-horz-center">
                        <HowDoesVerifyingWork verificationService={verificationService} />
                    </div>
                    <div className="sid-header__complete-required-fields sid-l-horz-center">
                        <FormattedHTMLMessage id="step.personalInfo.completeRequiredFields"
                            defaultMessage="Complete all required fields below. *" />
                    </div>
                </div>
            }

            <CountryComponentWrapper
                verificationService={verificationService}
                viewModel={viewModel}
            />

            <CaptainsCircleComponent
                value={getSafe(() => viewModel.metadata['captainsCircleNumber'], '')}
                onChange={
                    (newValue: any) => {
                        updateMedicalProfessionalViewModel('metadata', { ...verificationService.viewModel.metadata, 'captainsCircleNumber': newValue });
                        updateFieldValidationErrorsByFieldId('captainsCircleNumber' as FieldId, newValue.value, verificationService);
                    }
                }
                isErrored={!!fieldValidationErrors.captainsCircleNumber}
                errorId={fieldValidationErrors.captainsCircleNumber as any}
            />

            <MedicalStatus
                value={{
                    value: viewModel.status,
                    label: MedicalProfessionalStatusDefaultMessagesEnum[viewModel.status],
                }}
                isErrored={!!fieldValidationErrors.status}
                options={ getAvailableMedicalStatuses() }
                onChange={
                    (status: FormSelectChoice) => {
                        updateMedicalProfessionalViewModel('status', status ? (status.value as string) : '');
                        updateFieldValidationErrorsByFieldId('status', status ? (status.value as string) : '', verificationService);
                        if (status) {
                            setFocus('organization');
                        }
                    }
                }
                onKeyDown={event => handleMedicalStatusOnKeyDown(event)}
            />

            <MedicalProfessionalOrganization
                isErrored={!!fieldValidationErrors.organization}
                onChange={
                    (choice: Organization) => {
                        updateViewModelOrganization(choice, verificationService);
                        updateFieldValidationErrorsByFieldId('organization', choice, verificationService);
                    }
                }
                value={(verificationService.viewModel as MedicalProfessionalPersonalInfoViewModel).organization}
                verificationService={verificationService}
            />

            <div className="sid-names">
                <FirstName

                    value={viewModel.firstName}
                    isErrored={!!fieldValidationErrors.firstName}
                    onChange={
                        (newValue: string) => {
                            updateMedicalProfessionalViewModel('firstName', newValue);
                            updateFieldValidationErrorsByFieldId('firstName', newValue, verificationService);
                        }
                    }
                />

                <LastName

                    value={viewModel.lastName}
                    isErrored={!!fieldValidationErrors.lastName}
                    onChange={
                        (newValue: string) => {
                            updateMedicalProfessionalViewModel('lastName', newValue);
                            updateFieldValidationErrorsByFieldId('lastName', newValue, verificationService);
                        }
                    }
                />
            </div>

            <BirthDate
                isErrored={!!fieldValidationErrors.birthDate}
                errorId={fieldValidationErrors.birthDate}
                onChange={(newValue: string) => {
                    updateMedicalProfessionalViewModel('birthDate', newValue);
                    updateFieldValidationErrorsByFieldId('birthDate', newValue, verificationService);
                }}
                value={viewModel.birthDate}
            />



            <Email
                value={viewModel.email}
                isErrored={!!fieldValidationErrors.email}
                explanation={
                    <FormattedHTMLMessage
                        id="emailExplanation"
                        defaultMessage="Needed to send you your unique code"
                    />
                }
                onChange={
                    (newValue: string) => {
                        updateMedicalProfessionalViewModel('email', newValue);
                        updateFieldValidationErrorsByFieldId('email', newValue, verificationService);
                    }
                }
                onKeyDown={(event: any) => handleEmailOnKeyDown(event)}
            />
            {
                verificationService.programTheme.isSmsNotifierConfigured || verificationService.programTheme.smsLoopEnabled ?
                    <PhoneNumber
                        isRequired={!!verificationService.programTheme.smsLoopEnabled}
                        value={viewModel.phoneNumber}
                        isErrored={!!fieldValidationErrors.phoneNumber}
                        onChange={
                            (newValue: string) => {
                                updateMedicalProfessionalViewModel('phoneNumber', newValue);
                                updateFieldValidationErrorsByFieldId('phoneNumber', newValue, verificationService);
                            }
                        }
                    />
                    : null
            }

                <AddressComponent
                    isErrored={!!fieldValidationErrors.address1}
                    isRequired={true}
                    errorId={fieldValidationErrors.address1}
                    onChange={(newValue: string) => {
                        updateMedicalProfessionalViewModel('metadata', { ...verificationService.viewModel.metadata, 'address1': newValue });
                        updateFieldValidationErrorsByFieldId('address1', newValue, verificationService);
                    }}
                    value={viewModel.metadata ? viewModel.metadata.address1 : ''}
                />

                <div className="sid-thirds">

                    <CityComponent
                        isErrored={!!fieldValidationErrors.city}
                        isRequired={true}
                        errorId={fieldValidationErrors.city}
                        onChange={(newValue: string) => {
                            updateMedicalProfessionalViewModel('metadata', { ...verificationService.viewModel.metadata, 'city': newValue });
                            updateFieldValidationErrorsByFieldId('city', newValue, verificationService);
                        }}
                        value={viewModel.metadata ? viewModel.metadata.city : ''}
                    />

                    <StateComponent
                        isErrored={!!fieldValidationErrors.state}
                        isRequired={true}
                        errorId={fieldValidationErrors.state}
                        onChange={(newValue: string) => {
                            updateMedicalProfessionalViewModel('metadata', { ...verificationService.viewModel.metadata, 'state': newValue });
                            updateFieldValidationErrorsByFieldId('state', newValue, verificationService);
                        }}
                        value={getSafe(() => viewModel.metadata['state'], '')}
                    />

                    <PostalCode
                        isErrored={!!fieldValidationErrors.postalCode}
                        isRequired={true}
                        onChange={(newValue: string) => {
                            updateMedicalProfessionalViewModel('postalCode', newValue);
                            updateFieldValidationErrorsByFieldId('postalCode', newValue, verificationService);
                        }}
                        value={viewModel.postalCode}
                    />

                </div>
                <div className="sid-authorization-on-submit">
                    <FormattedHTMLMessage
                        id='step.personalInfo.authorizationDisclaimer'
                        defaultMessage='By completing this form and verifying my status I authorize Princess Cruises to email me, however I am able to unsubscribe at any time. For more details, see the <a href="https://www.princess.com/legal/legal_privacy/">Princess Privacy & Cookies Notice</a>. This is not a preference center. If you wish to make changes to your email or other information please go to <a href="https://book.princess.com/captaincircle/myPrincess.page">My Princess</a>.' />
                </div>

            <MarketConsentWrapper
                verificationService={verificationService}
                isErrored={!!fieldValidationErrors.marketConsentValue}
                onChange={(newValue) => {
                    updateMedicalProfessionalViewModel('metadata', { ...verificationService.viewModel.metadata, marketConsentValue: newValue });
                    updateFieldValidationErrorsByFieldId('marketConsentValue', newValue, verificationService);
                }}
                viewModel={viewModel}
            />

            <div className="sid-form-region sid-submit-wrapper sid-l-space-top-md">
                <div className="sid-submit">
                    <button
                        id="sid-submit-btn-collect-info"
                        onClick={() => submitForm(viewModel, verificationService, VerificationStepsEnum.collectMedicalProfessionalPersonalInfo)}
                        type="submit"
                        className={`sid-btn sid-btn--dark sid-l-full-width ${!isFormFilled(viewModel, verificationService.formValidationOptions) ? 'sid-btn--disabled-like' : ''}`}
                        aria-labelledby="verify-status-text"
                        ref={button => setRef('submitButton', button as HTMLButtonElement)}
                    >
                        <span id="verify-status-text">
                            { failedInstantMatch ?
                                <FormattedHTMLMessage id="tryAgain" defaultMessage="Try Again" />
                            :
                                <FormattedHTMLMessage id="verifyMyMedicalProfessionalStatus" defaultMessage="Verify My Medical Professional Status" />
                            }
                        </span>
                    </button>
                </div>
            </div>

            <FormFooter verificationService={verificationService} />
        </div>
    );
};

export const StepMedicalProfessionalPersonalInfoComponent = injectIntl(StepMedicalProfessionalPersonalInfo);
